import React, { useState } from 'react';
import { X, UserPlus } from 'lucide-react';
import { apiService, Customer } from '../services/api';

interface CreateCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (customer: Customer) => void;
}

export const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [whatsappName, setWhatsappName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const resetForm = () => {
    setName('');
    setWhatsappName('');
    setPhoneNumber('');
    setErrorMessage('');
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');

    if (!cleanPhone) {
      setErrorMessage('Phone number is required.');
      return;
    }

    setSaving(true);
    setErrorMessage('');

    try {
      const created = await apiService.createCustomer({
        name: name.trim() || undefined,
        whatsappName: whatsappName.trim() || undefined,
        phoneNumber: cleanPhone,
      });
      onSave(created);
      handleClose();
    } catch (error: any) {
      const serverMessage = error?.response?.data?.error;
      const responseStatus = error?.response?.status;
      const fallbackMessage =
        serverMessage ||
        error?.response?.data?.message ||
        error?.message ||
        '';

      const isDuplicate =
        responseStatus === 409 ||
        /already exists|duplicate|e11000/i.test(String(fallbackMessage));

      if (isDuplicate) {
        try {
          const customers = await apiService.getCustomers();
          const existingCustomer = customers.find((customer) => {
            const customerPhone = (customer.phoneNumber || '').replace(/\D/g, '');
            const customerWhatsapp = (customer.whatsappId || '').split('@')[0].replace(/\D/g, '');
            return customerPhone === cleanPhone || customerWhatsapp === cleanPhone;
          });

          if (existingCustomer) {
            onSave(existingCustomer);
            handleClose();
            return;
          }
        } catch (lookupError) {
          // Fall through to generic error below.
          console.error('Failed to fetch existing customer after duplicate response:', lookupError);
        }
      }

      setErrorMessage(serverMessage || 'Failed to create customer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Customer</h3>
          <button className="ghost-button" onClick={handleClose} disabled={saving}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label>Lead Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter lead name"
              className="input-field"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>WhatsApp Name (optional)</label>
            <input
              type="text"
              value={whatsappName}
              onChange={(e) => setWhatsappName(e.target.value)}
              placeholder="From WhatsApp contact if known"
              className="input-field"
            />
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="e.g. 201234567890 (digits only)"
              className="input-field"
            />
            <p className="form-hint">WhatsApp ID will be generated automatically from this number.</p>
          </div>

          {errorMessage && (
            <div className="form-group">
              <span style={{ color: '#b42318', fontSize: '0.85rem' }}>{errorMessage}</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="ghost-button" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
          <button className="primary-button" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner small" style={{ width: 16, height: 16 }}></span>
                Creating...
              </>
            ) : (
              <>
                Create Customer
                <UserPlus size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
